<div class="dashboard-content-sec" *ngIf="authorised">
    <div class="loader" *ngIf="committing || undoingId !== null">
        <img src="assets/images/loader.gif" alt="">
    </div>

    <div class="card mb-3">
        <app-breadcrumb [crumbList]="crumbList"></app-breadcrumb>
        <div class="user-heading-sec">
            <span class="p-2">Ticket Reassignment</span>
        </div>

        <div class="card-body">
            <p class="text-muted">
                Move tickets from one account to another attorney, for example when an attorney leaves.
                Check the email address and role carefully: the same person can have more than one account.
            </p>

            <!-- Step 1: choose -->
            <div class="form-row">
                <div class="form-group col-md-6">
                    <label for="reassignSourceSearch"><strong>Move tickets from</strong></label>
                    <div *ngIf="sourcesLoading" class="text-muted">Loading users with assigned tickets...</div>
                    <div *ngIf="!sourcesLoading && sourcesError" class="alert alert-danger" role="alert">
                        {{ sourcesError }}
                        <a href="javascript:void(0)" (click)="loadSources()">Try again</a>
                    </div>
                    <div *ngIf="!sourcesLoading && !sourcesError && sources.length === 0" class="text-muted">
                        There are no assigned tickets.
                    </div>
                    <ng-container *ngIf="!sourcesLoading && !sourcesError && sources.length > 0">
                        <input type="text" id="reassignSourceSearch" class="form-control reassign-filter"
                               placeholder="Search name, email or ID" aria-controls="reassignSourceList"
                               autocomplete="off" data-bwignore="true" data-lpignore="true" data-1p-ignore="true"
                               [ngModel]="sourceSearch" (ngModelChange)="onSourceSearch($event)">
                        <div class="reassign-picker-links">
                            <button type="button" class="btn btn-link p-0" [disabled]="busy || atSourceLimit()"
                                    (click)="selectAllShown()">Select all shown</button>
                            <button type="button" class="btn btn-link p-0" [disabled]="busy || selectedCount === 0"
                                    (click)="clearSources()">Clear</button>
                            <span class="reassign-picker-count">
                                {{ formatCount(visibleRealSources.length + visibleDeletedSources.length) }}
                                {{ visibleRealSources.length + visibleDeletedSources.length === 1 ? 'account' : 'accounts' }} listed<ng-container
                                    *ngIf="visibleRealSources.length + visibleDeletedSources.length > 7">. Scroll the list to see them all</ng-container>
                            </span>
                        </div>
                        <div id="reassignSourceList" class="reassign-picker" role="group" aria-label="Accounts to move tickets from">
                            <div *ngFor="let s of visibleRealSources" class="form-check reassign-picker-row">
                                <input class="form-check-input" type="checkbox" [id]="'reassignSource' + s.UserID"
                                       [checked]="isSourceSelected(s.UserID)"
                                       [disabled]="busy || (!isSourceSelected(s.UserID) && atSourceLimit())"
                                       (change)="toggleSource(s, $event)">
                                <label class="form-check-label" [attr.for]="'reassignSource' + s.UserID">{{ sourceLabel(s) }}</label>
                            </div>
                            <div *ngIf="visibleDeletedSources.length > 0" class="reassign-picker-group" role="group"
                                 aria-labelledby="reassignDeletedHeading">
                                <div id="reassignDeletedHeading" class="reassign-picker-heading">Accounts that no longer exist</div>
                                <small class="reassign-hint mt-0 mb-1">
                                    Tickets still assigned to accounts that were deleted. Nobody is notified on these until they are moved.
                                </small>
                                <div *ngFor="let s of visibleDeletedSources" class="form-check reassign-picker-row">
                                    <input class="form-check-input" type="checkbox" [id]="'reassignSource' + s.UserID"
                                           [checked]="isSourceSelected(s.UserID)"
                                           [disabled]="busy || (!isSourceSelected(s.UserID) && atSourceLimit())"
                                           (change)="toggleSource(s, $event)">
                                    <label class="form-check-label" [attr.for]="'reassignSource' + s.UserID">{{ sourceLabel(s) }}</label>
                                </div>
                            </div>
                            <div *ngIf="visibleRealSources.length === 0 && visibleDeletedSources.length === 0" class="reassign-hint mt-0">
                                No accounts match this search.
                            </div>
                        </div>
                        <small class="reassign-hint" aria-live="polite">{{ selectionSummary() }}</small>
                        <small *ngIf="atSourceLimit()" class="reassign-hint">
                            You can move tickets from up to {{ maxSources }} accounts at a time. Untick one to choose another.
                        </small>
                    </ng-container>
                </div>

                <div class="form-group col-md-6">
                    <label for="reassignTo"><strong>Assign to</strong></label>
                    <div *ngIf="targetsLoading" class="text-muted">Loading attorneys...</div>
                    <div *ngIf="!targetsLoading && targetsError" class="alert alert-danger" role="alert">
                        {{ targetsError }}
                        <a href="javascript:void(0)" (click)="loadTargets()">Try again</a>
                    </div>
                    <div *ngIf="!targetsLoading && !targetsError && targets.length === 0" class="text-muted">
                        There are no active attorneys to assign tickets to.
                    </div>
                    <select *ngIf="!targetsLoading && !targetsError && targets.length > 0"
                            id="reassignTo" class="form-control"
                            [ngModel]="toUserId" (ngModelChange)="onToChange($event)"
                            [disabled]="busy || selectedCount === 0">
                        <option [ngValue]="null">Select an attorney</option>
                        <option *ngFor="let t of availableTargets" [ngValue]="t.UserID">{{ targetLabel(t) }}</option>
                    </select>
                </div>
            </div>

            <div class="form-group">
                <label class="d-block"><strong>Ticket statuses to move</strong></label>
                <div class="form-check form-check-inline" *ngFor="let opt of statusOptions">
                    <input class="form-check-input" type="checkbox" id="reassignStatus{{ opt.value }}"
                           [checked]="opt.checked"
                           [disabled]="busy || (opt.checked && checkedCount === 1)"
                           (change)="toggleStatus(opt, $event)">
                    <label class="form-check-label" for="reassignStatus{{ opt.value }}">{{ opt.label }}</label>
                </div>
                <small class="reassign-hint">
                    At least one status must stay ticked. With all three ticked, every ticket is moved, including any with an unusual status.
                </small>
                <div *ngIf="matchingCount() === 0" class="text-danger mt-1">
                    The selected accounts have no tickets with the ticked statuses.
                </div>
            </div>

            <div class="form-group">
                <label for="reassignReason"><strong>Reason for reassignment</strong></label>
                <textarea id="reassignReason" class="form-control" rows="3"
                          [attr.maxlength]="reasonMax"
                          [ngModel]="reason" (ngModelChange)="reason = $event"
                          [disabled]="busy"></textarea>
                <div class="reassign-reason-meter" [class.reassign-reason-ok]="reasonValid()">
                    <span>
                        <i *ngIf="reasonValid()" class="fas fa-check-circle" aria-hidden="true"></i>
                        <ng-container *ngIf="reasonValid()">Minimum reached</ng-container>
                        <ng-container *ngIf="!reasonValid()">{{ reasonMin - trimmedReason().length }} more {{ reasonMin - trimmedReason().length === 1 ? 'character' : 'characters' }} needed</ng-container>
                    </span>
                    <span>{{ trimmedReason().length }} / {{ reasonMax }}</span>
                </div>
                <small class="reassign-hint">
                    Required, at least {{ reasonMin }} characters. This is included in the email to the receiving attorney and kept in the history.
                </small>
            </div>

            <!-- Step 2: preview -->
            <div class="mb-3">
                <button type="button" class="btn btn-default" [disabled]="!canPreview()" (click)="runPreview()">
                    {{ previewLoading ? 'Loading preview...' : 'Preview' }}
                </button>
            </div>

            <div *ngIf="previewError" class="alert alert-danger" role="alert">{{ previewError }}</div>

            <!-- Step 3: confirm -->
            <div *ngIf="preview" class="card reassign-preview">
                <div class="card-body">
                    <h5 class="card-title">Preview</h5>
                    <p class="mb-1"><strong>From</strong></p>
                    <div class="reassign-table-wrap mb-3">
                        <table class="table table-sm reassign-preview-sources">
                            <thead>
                                <tr>
                                    <th>Account</th>
                                    <th class="text-right">New</th>
                                    <th class="text-right">Active</th>
                                    <th class="text-right">Closed</th>
                                    <th *ngIf="previewHasOther()" class="text-right">Other</th>
                                    <th class="text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let s of preview.sources" [class.reassign-muted]="!s.counts || s.counts.total === 0">
                                    <td class="reassign-person">
                                        <ng-container *ngIf="s.deleted">{{ deletedLabel(s.UserID) }}</ng-container>
                                        <ng-container *ngIf="!s.deleted">
                                            {{ fullName(s) }}
                                            <small class="reassign-sub">{{ s.EMail || 'no email' }}</small>
                                            <small class="reassign-sub">{{ roleName(s.RoleType) }} ({{ isActive(s) ? 'Active' : 'Inactive' }})</small>
                                        </ng-container>
                                        <small *ngIf="!s.counts || s.counts.total === 0" class="reassign-sub">nothing to move</small>
                                    </td>
                                    <td class="text-right">{{ formatCount(s.counts?.new) }}</td>
                                    <td class="text-right">{{ formatCount(s.counts?.active) }}</td>
                                    <td class="text-right">{{ formatCount(s.counts?.closed) }}</td>
                                    <td *ngIf="previewHasOther()" class="text-right">{{ formatCount(s.counts?.other) }}</td>
                                    <td class="text-right">{{ formatCount(s.counts?.total) }}</td>
                                </tr>
                                <tr class="reassign-preview-total">
                                    <td><strong>Total</strong></td>
                                    <td class="text-right"><strong>{{ formatCount(preview.counts.new) }}</strong></td>
                                    <td class="text-right"><strong>{{ formatCount(preview.counts.active) }}</strong></td>
                                    <td class="text-right"><strong>{{ formatCount(preview.counts.closed) }}</strong></td>
                                    <td *ngIf="previewHasOther()" class="text-right"><strong>{{ formatCount(preview.counts.other) }}</strong></td>
                                    <td class="text-right"><strong>{{ formatCount(preview.counts.total) }}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p class="mb-1"><strong>To</strong></p>
                    <p class="mb-1">{{ fullName(preview.to) }}</p>
                    <p class="mb-3">{{ preview.to.EMail || 'no email' }}</p>

                    <p class="mb-3"><strong>Tickets included:</strong> {{ statusesLabel(previewRequest.statuses) }}</p>

                    <p *ngIf="preview.counts.total === 0" class="text-muted">There are no tickets to move for this selection.</p>
                    <button *ngIf="preview.counts.total > 0" type="button" class="btn btn-danger"
                            [disabled]="busy || !reasonValid()" (click)="confirmCommit()">
                        {{ committing ? 'Reassigning...' : 'Reassign ' + formatCount(preview.counts.total) + ' ' + ticketWord(preview.counts.total) }}
                    </button>
                    <small *ngIf="preview.counts.total > 0 && !busy && !reasonValid()" class="reassign-hint">
                        Enter a reason of at least {{ reasonMin }} characters to continue.
                    </small>
                </div>
            </div>
        </div>
    </div>

    <!-- Tickets needing attention: read only, loaded separately so the form never waits for it. -->
    <div class="card mb-3" *ngIf="showAttentionCard()">
        <div class="user-heading-sec">
            <span class="p-2 reassign-attention-heading">Tickets needing attention</span>
            <span *ngIf="attentionLoading && attention" class="reassign-hint d-inline">Checking...</span>
        </div>
        <div class="card-body">
            <div *ngIf="attentionLoading && !attention" class="text-muted">Checking...</div>
            <div *ngIf="!attentionLoading && attentionError" class="alert alert-danger mb-0" role="alert">
                {{ attentionError }}
                <a href="javascript:void(0)" (click)="loadAttention()">Try again</a>
            </div>

            <ng-container *ngIf="attention && !attentionError">
                <div *ngIf="attention.hidden.total > 0" class="reassign-attention-block">
                    <p class="reassign-attention-title">Tickets with missing records</p>
                    <p class="mb-2">{{ hiddenSentence() }}</p>
                    <ng-container *ngIf="attention.hidden.open.total > 0">
                        <table class="table table-sm reassign-attention-causes">
                            <thead>
                                <tr>
                                    <th>Open tickets by cause</th>
                                    <th class="text-right">Tickets</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td>Missing requester</td><td class="text-right">{{ formatCount(attention.hidden.causes_open?.requester) }}</td></tr>
                                <tr><td>Missing subject</td><td class="text-right">{{ formatCount(attention.hidden.causes_open?.subject) }}</td></tr>
                                <tr><td>Missing client</td><td class="text-right">{{ formatCount(attention.hidden.causes_open?.client) }}</td></tr>
                            </tbody>
                        </table>
                        <small class="reassign-hint mt-0 mb-2">A ticket can have more than one cause.</small>
                    </ng-container>
                    <div class="reassign-attention-actions">
                        <button type="button" class="btn btn-default btn-sm" [disabled]="!canDownloadAttention('open')"
                                (click)="downloadAttention('open')">
                            {{ attentionDownloading === 'open' ? 'Preparing...' : 'Download open tickets (CSV)' }}
                        </button>
                        <button *ngIf="attention.hidden.total !== attention.hidden.open.total" type="button"
                                class="btn btn-default btn-sm" [disabled]="!canDownloadAttention('all')"
                                (click)="downloadAttention('all')">
                            {{ attentionDownloading === 'all' ? 'Preparing...' : 'Download all (CSV)' }}
                        </button>
                    </div>
                    <small class="reassign-hint">
                        Fixing the underlying record, for example re-creating the client, makes the placeholder go away.
                    </small>
                </div>

                <div *ngIf="attention.deleted_accounts.tickets > 0" class="reassign-attention-block">
                    <p class="reassign-attention-title">Assigned to accounts that no longer exist</p>
                    <p class="mb-2">{{ deletedAccountsSentence() }}</p>
                    <button type="button" class="btn btn-default btn-sm" [disabled]="!canSelectDeleted()"
                            (click)="selectDeletedAccounts()">Select these accounts</button>
                </div>
            </ng-container>
        </div>
    </div>

    <!-- History -->
    <div class="card mb-0">
        <div class="user-heading-sec">
            <span class="p-2">Reassignment History</span>
        </div>

        <div class="px-3 pt-3">
            <div class="form-row">
                <div class="form-group col-6 col-md-4 col-lg-2">
                    <label for="historyDateFrom">From date</label>
                    <input type="date" id="historyDateFrom" class="form-control reassign-filter"
                           [ngModel]="filterDateFrom" (ngModelChange)="filterDateFrom = $event; onFilterChange()">
                </div>
                <div class="form-group col-6 col-md-4 col-lg-2">
                    <label for="historyDateTo">To date</label>
                    <input type="date" id="historyDateTo" class="form-control reassign-filter"
                           [ngModel]="filterDateTo" (ngModelChange)="filterDateTo = $event; onFilterChange()">
                </div>
                <div class="form-group col-6 col-md-4 col-lg-2">
                    <label for="historyFrom">From</label>
                    <input type="text" id="historyFrom" class="form-control reassign-filter" placeholder="Name, email or ID"
                           autocomplete="off" data-bwignore="true" data-lpignore="true" data-1p-ignore="true"
                           [ngModel]="filterFrom" (ngModelChange)="filterFrom = $event; onTextFilterChange()">
                </div>
                <div class="form-group col-6 col-md-4 col-lg-2">
                    <label for="historyTo">To</label>
                    <input type="text" id="historyTo" class="form-control reassign-filter" placeholder="Name, email or ID"
                           autocomplete="off" data-bwignore="true" data-lpignore="true" data-1p-ignore="true"
                           [ngModel]="filterTo" (ngModelChange)="filterTo = $event; onTextFilterChange()">
                </div>
                <div class="form-group col-6 col-md-4 col-lg-2">
                    <label for="historyState">State</label>
                    <select id="historyState" class="form-control reassign-filter"
                            [ngModel]="filterState" (ngModelChange)="filterState = $event; onFilterChange()">
                        <option [ngValue]="null">Any</option>
                        <option [ngValue]="'done'">Done</option>
                        <option [ngValue]="'undone'">Undone</option>
                    </select>
                </div>
                <div class="form-group col-6 col-md-4 col-lg-2">
                    <label for="historyStatus">Statuses</label>
                    <select id="historyStatus" class="form-control reassign-filter"
                            [ngModel]="filterStatus" (ngModelChange)="filterStatus = $event; onFilterChange()">
                        <option [ngValue]="null">Any</option>
                        <option [ngValue]="1">New</option>
                        <option [ngValue]="2">Active</option>
                        <option [ngValue]="3">Closed</option>
                    </select>
                </div>
            </div>
            <small *ngIf="dateRangeInvalid()" class="reassign-hint mt-0 mb-2">From date must be on or before To date.</small>

            <div class="reassign-history-bar mb-2">
                <span>
                    <ng-container *ngIf="!dateRangeInvalid() && !historyError && historyPageInfo && historyPageInfo.total > 0 && history.length > 0">
                        Showing {{ formatCount(historyFirstRow()) }} to {{ formatCount(historyLastRow()) }} of {{ formatCount(historyPageInfo.total) }}
                    </ng-container>
                    <span *ngIf="historyLoading" class="reassign-hint d-inline ml-2">Loading...</span>
                </span>
                <span class="reassign-top-pager" *ngIf="!dateRangeInvalid() && !historyError">
                    <ng-container *ngTemplateOutlet="historyPager"></ng-container>
                </span>
                <span class="reassign-history-actions">
                    <button *ngIf="hasFilters()" type="button" class="btn btn-default btn-sm" (click)="clearFilters()">Clear filters</button>
                    <button type="button" class="btn btn-default btn-sm" [disabled]="!canExport()" (click)="downloadCsv()">
                        {{ exporting ? 'Preparing...' : 'Download CSV' }}
                    </button>
                </span>
            </div>
            <small class="reassign-hint reassign-export-hint mt-0 mb-2">
                {{ hasFilters() ? 'Exports every row matching the filters, not only this page.' : 'Exports the full history, not only this page.' }}
            </small>
        </div>

        <div class="table-responsive" *ngIf="!dateRangeInvalid()">
            <div *ngIf="historyError" class="alert alert-danger m-3" role="alert">
                {{ historyError }}
                <a href="javascript:void(0)" (click)="loadHistory()">Try again</a>
            </div>
            <div class="reassign-table-wrap" *ngIf="!historyError">
            <table class="table user-table table-striped reassign-history">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Tickets</th>
                        <th>Reason</th>
                        <th>State</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let b of history">
                        <td>
                            <div class="reassign-date">
                                {{ b.performed_at | date:'MMM d, y' }}<br>{{ b.performed_at | date:'h:mm a' }}
                            </div>
                            <small class="reassign-sub">by {{ fullName(b.performed_by) }}</small>
                        </td>
                        <td class="reassign-person">
                            <ng-container *ngIf="b.from_deleted || !b.from_user">
                                Deleted account<small class="reassign-sub">ID {{ b.from_user_id }}</small>
                            </ng-container>
                            <ng-container *ngIf="!b.from_deleted && b.from_user">
                                {{ fullName(b.from_user) }}<small class="reassign-sub">{{ b.from_user.EMail }}</small>
                            </ng-container>
                        </td>
                        <td class="reassign-person">{{ fullName(b.to_user) }}<small class="reassign-sub">{{ b.to_user?.EMail }}</small></td>
                        <td>{{ formatCount(b.moved) }}<small class="reassign-sub">{{ historyStatusesLabel(b.statuses) }}</small></td>
                        <td><div class="reassign-reason">{{ b.reason || '-' }}</div></td>
                        <td>
                            <div class="reassign-state">
                                <ng-container *ngIf="!b.undone_at">
                                    Done
                                    <div class="mt-1">
                                        <button type="button" class="btn btn-default btn-sm"
                                                [disabled]="busy" (click)="confirmUndo(b)">
                                            {{ undoingId === b.id ? 'Undoing...' : 'Undo' }}
                                        </button>
                                    </div>
                                </ng-container>
                                <ng-container *ngIf="b.undone_at">
                                    Undone, {{ formatCount(b.restored) }} restored
                                    <small class="reassign-sub">{{ b.undone_at | date:'MMM d, y h:mm a' }}</small>
                                    <small class="reassign-sub">by {{ fullName(b.undone_by) }}</small>
                                </ng-container>
                            </div>
                        </td>
                    </tr>
                    <tr *ngIf="!historyLoading && history.length === 0">
                        <td colspan="6">{{ hasFilters() ? 'No reassignments match these filters.' : 'No reassignments yet.' }}</td>
                    </tr>
                </tbody>
            </table>
            </div>
            <div class="has-text-centered align-items-end" *ngIf="!historyError">
                <ng-container *ngTemplateOutlet="historyPager"></ng-container>
            </div>
        </div>
    </div>

    <!-- One pager definition, rendered above and below the history table. -->
    <ng-template #historyPager>
        <ul *ngIf="pager.pages && pager.pages.length && pager.pages.length>1" class="pagination">
            <li *ngIf="pager.currentPage !== 1" [ngClass]="{disabled:pager.currentPage === 1}">
                <a (click)="setHistoryPage(1)">First</a>
            </li>
            <li *ngIf="pager.currentPage !== 1" [ngClass]="{disabled:pager.currentPage === 1}">
                <a (click)="setHistoryPage(pager.currentPage - 1)">Previous</a>
            </li>
            <li *ngFor="let page of pager.pages" [ngClass]="{active:pager.currentPage === page}">
                <a (click)="setHistoryPage(page)">{{page}}</a>
            </li>
            <li *ngIf="pager.currentPage !== pager.totalPages" [ngClass]="{disabled:pager.currentPage === pager.totalPages}">
                <a (click)="setHistoryPage(pager.currentPage + 1)">Next</a>
            </li>
            <li *ngIf="pager.currentPage !== pager.totalPages" [ngClass]="{disabled:pager.currentPage === pager.totalPages}">
                <a (click)="setHistoryPage(pager.totalPages)">Last</a>
            </li>
        </ul>
    </ng-template>
</div>
